/**
 * Toasts: short, non-blocking notices (new proposal, report arrived). They stack at the top, dismiss
 * themselves after a few seconds, and are announced politely to screen readers.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence } from 'motion/react';
import * as motion from 'motion/react-m';
import { Icon, type IconName } from './Icon';
import { IconButton } from './Button';
import { cx } from './cx';

export type ToastTone = 'info' | 'good' | 'warn' | 'bad';

export interface ToastInput {
  title: string;
  text?: string;
  tone?: ToastTone;
  icon?: IconName;
  /** ms before it closes by itself; 0 keeps it until dismissed. */
  duration?: number;
  /** Optional action button. */
  action?: { label: string; onClick: () => void };
}

interface ToastItem extends ToastInput {
  id: number;
}

interface ToastApi {
  show: (t: ToastInput) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const MAX_TOASTS = 4;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const next = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  // At most MAX_TOASTS show at once. Extra toasts wait their turn instead of being dropped, so a burst of
  // notices at dawn (Assembly results, a crisis, the news) never hides one of them.
  const shown = useRef<number[]>([]);
  const waiting = useRef<ToastItem[]>([]);

  const reveal = useRef<(t: ToastItem) => void>(() => undefined);

  const dismiss = useCallback((id: number) => {
    const t = timers.current.get(id);
    if (t) clearTimeout(t);
    timers.current.delete(id);
    waiting.current = waiting.current.filter((x) => x.id !== id);
    if (!shown.current.includes(id)) return;
    shown.current = shown.current.filter((x) => x !== id);
    setItems((xs) => xs.filter((x) => x.id !== id));
    const nextUp = waiting.current.shift();
    if (nextUp) reveal.current(nextUp);
  }, []);

  reveal.current = (t: ToastItem) => {
    shown.current = [...shown.current, t.id];
    setItems((xs) => [...xs, t]);
    const duration = t.duration ?? (t.tone === 'bad' ? 8000 : 5000);
    if (duration > 0) timers.current.set(t.id, setTimeout(() => dismiss(t.id), duration));
  };

  const show = useCallback((t: ToastInput) => {
    const item: ToastItem = { ...t, id: next.current++ };
    if (shown.current.length < MAX_TOASTS) reveal.current(item);
    else waiting.current.push(item);
    return item.id;
  }, []);

  useEffect(() => {
    const map = timers.current;
    return () => {
      for (const t of map.values()) clearTimeout(t);
      map.clear();
    };
  }, []);

  const api = useMemo(() => ({ show, dismiss }), [show, dismiss]);
  return (
    <ToastContext.Provider value={api}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(
          <div className="toasts" role="region" aria-label="Notifications" aria-live="polite">
            <AnimatePresence initial={false}>
              {items.map((t) => (
                <motion.div
                  key={t.id}
                  layout
                  className={cx('toast', t.tone && t.tone !== 'info' && `toast--${t.tone}`)}
                  initial={{ opacity: 0, y: -12, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, x: 40 }}
                  transition={{ type: 'spring', stiffness: 460, damping: 36 }}
                  role={t.tone === 'bad' ? 'alert' : 'status'}
                >
                  <Icon name={t.icon ?? (t.tone === 'good' ? 'check' : t.tone === 'bad' || t.tone === 'warn' ? 'warning' : 'info')} className="toast__icon" size={20} />
                  <div className="toast__body">
                    <p className="toast__title">{t.title}</p>
                    {t.text && <p className="toast__text">{t.text}</p>}
                    {t.action && (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        style={{ marginTop: 4, marginLeft: -12 }}
                        onClick={() => {
                          t.action?.onClick();
                          dismiss(t.id);
                        }}
                      >
                        {t.action.label}
                      </button>
                    )}
                  </div>
                  <IconButton icon="close" label="Dismiss" size={18} onClick={() => dismiss(t.id)} />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>,
          document.body,
        )}
    </ToastContext.Provider>
  );
}

/** Show and dismiss toasts from anywhere under ToastProvider. */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>.');
  return ctx;
}
